import { AnyAction, createReducer } from '@reduxjs/toolkit';
import {
	clearSensorIdsMap,
	getVirtualSensors,
	getVirtualSensorsRejected,
	getVirtualSensorsResolved,
	getSensor,
	getSensorIdsMap,
	getSensorIdsMapRejected,
	getSensorIdsMapResolved,
	getSensorRejected,
	getSensorResolved,
	mapConfigurationToSensor,
	getSensorById,
	getSensorByIdResolved,
	getSensorByIdRejected,
	getSensorsLatestData,
	getSensorsLatestDataResolved,
	getSensorsLatestDataRejected,
	getSensorsHealthStatusLegacy,
	getSensorsHealthStatusResolvedLegacy,
	getSensorsHealthStatusRejectedLegacy,
	getSensorsHealthStatus,
	getSensorsHealthStatusResolved,
	getSensorsHealthStatusRejected,
} from '@Actions';

import { STATUS_ENUM } from '@Types/editSensorData.types';
import { SensorsStoreState } from '@Types/sensors.types';
import { Configuration } from '@Types';

export const initialState: SensorsStoreState = {
	initialized: false,
	status: 'idle',
	list: [],
	sensorIdMap: {
		status: STATUS_ENUM.IDLE,
		sensorIdMap: {},
	},
	virtualSensors: {
		status: STATUS_ENUM.IDLE,
		sensors: [],
	},
	sensorById: {
		status: STATUS_ENUM.IDLE,
	},
	sensorsLatestUptimeData: {
		status: STATUS_ENUM.IDLE,
		sensorsMap: {},
	},
	sensorsHealthStatus: {
		status: STATUS_ENUM.IDLE,
		sensorsMap: {},
	},
};

interface Sensors {
	[x: string]: (
		state: SensorsStoreState,
		action: AnyAction,
	) => SensorsStoreState | undefined | void;
}

const reducer: Sensors = {
	[getSensor.toString()]: state => {
		state.initialized = false;
	},
	[getSensorResolved.toString()]: (state, action) => {
		return {
			...state,
			initialized: true,
			list: [action.payload.sensor],
		};
	},
	[getSensorRejected.toString()]: state => {
		return {
			...state,
			initialized: true,
		};
	},
	[mapConfigurationToSensor.toString()]: (state, action) => {
		const { sensorId, sensorAlias, latitude, longitude } =
			action.payload as Configuration;
		const index = state.list.findIndex(s => s.sensorId === sensorId);
		if (index !== -1) {
			if (latitude && longitude) {
				state.list[index].geometry = {
					type: state.list[index].geometry?.type || 'POINT',
					coordinates: [longitude, latitude],
				};
			}
			state.list[index].alias = sensorAlias;
		}
	},
	[getSensorIdsMap.toString()]: state => ({
		...state,
		sensorIdMap: {
			status: STATUS_ENUM.PENDING,
			sensorIdMap: {},
		},
	}),
	[clearSensorIdsMap.toString()]: state => ({
		...state,
		sensorIdMap: {
			status: STATUS_ENUM.IDLE,
			sensorIdMap: {},
		},
	}),
	[getSensorIdsMapResolved.toString()]: (state, params) => ({
		...state,
		sensorIdMap: {
			status: STATUS_ENUM.SUCCESS,
			sensorIdMap: params.payload,
		},
	}),
	[getSensorIdsMapRejected.toString()]: state => ({
		...state,
		sensorIdMap: {
			...state.sensorIdMap,
			status: STATUS_ENUM.ERROR,
		},
	}),
	[getVirtualSensors.toString()]: state => ({
		...state,
		virtualSensors: {
			status: STATUS_ENUM.PENDING,
			sensors: [],
		},
	}),
	[getVirtualSensorsResolved.toString()]: (state, action: AnyAction) => ({
		...state,
		virtualSensors: {
			status: STATUS_ENUM.SUCCESS,
			sensors: action.payload,
		},
	}),
	[getVirtualSensorsRejected.toString()]: state => ({
		...state,
		virtualSensors: {
			status: STATUS_ENUM.ERROR,
			sensors: [],
		},
	}),
	[getSensorById.toString()]: state => ({
		...state,
		sensorById: {
			status: STATUS_ENUM.PENDING,
			sensor: undefined,
		},
	}),
	[getSensorByIdResolved.toString()]: (state, action: AnyAction) => ({
		...state,
		sensorById: {
			status: STATUS_ENUM.SUCCESS,
			sensor: action.payload.sensor,
		},
	}),
	[getSensorByIdRejected.toString()]: state => ({
		...state,
		sensorById: {
			status: STATUS_ENUM.ERROR,
			sensor: undefined,
		},
	}),
	[getSensorsLatestData.toString()]: state => {
		state.sensorsLatestUptimeData = {
			status: STATUS_ENUM.PENDING,
			sensorsMap: {},
		};
	},
	[getSensorsLatestDataResolved.toString()]: (state, action) => {
		state.sensorsLatestUptimeData = {
			status: STATUS_ENUM.SUCCESS,
			sensorsMap: action.payload,
		};
	},
	[getSensorsLatestDataRejected.toString()]: state => {
		state.sensorsLatestUptimeData = {
			status: STATUS_ENUM.ERROR,
			sensorsMap: {},
		};
	},
	[getSensorsHealthStatus.toString()]: state => {
		state.sensorsHealthStatus = {
			status: STATUS_ENUM.PENDING,
			sensorsMap: {},
		};
	},
	[getSensorsHealthStatusResolved.toString()]: (state, action) => {
		state.sensorsHealthStatus = {
			status: STATUS_ENUM.SUCCESS,
			sensorsMap: action.payload,
		};
	},
	[getSensorsHealthStatusRejected.toString()]: state => {
		state.sensorsHealthStatus = {
			status: STATUS_ENUM.ERROR,
			sensorsMap: {},
		};
	},
	[getSensorsHealthStatusLegacy.toString()]: state => {
		state.sensorsHealthStatus = {
			status: STATUS_ENUM.PENDING,
			sensorsMap: {},
		};
	},
	[getSensorsHealthStatusResolvedLegacy.toString()]: (state, action) => {
		state.sensorsHealthStatus = {
			status: STATUS_ENUM.SUCCESS,
			sensorsMap: action.payload,
		};
	},
	[getSensorsHealthStatusRejectedLegacy.toString()]: state => {
		state.sensorsHealthStatus = {
			status: STATUS_ENUM.ERROR,
			sensorsMap: {},
		};
	},
};

export default createReducer(initialState, reducer);
